import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {listProductsInfinite, getCategories, getCategoryHeroMeta, type CatalogFilterGroup, type Category, type Product, type ProductListParams} from "@zaaaz/shared";
import {DsEmptyState, DsListingToolbar} from "@zaaaz/ui";
import {ProductCard} from "./ProductCard";
import {ProductFilters, type FilterChange} from "./ProductFilters";
import {ProductActiveFilters} from "./ProductActiveFilters";
import {CategorySubnavGrid} from "./CategorySubnavGrid";
import {CategoryPageHero} from "./CategoryPageHero";
import {ProductGridSkeleton} from "./ShopSkeleton";
import {useI18n} from "./I18nProvider";
import {useDocumentTitle} from "@/hooks/useDocumentTitle";
import type {I18nKey} from "@zaaaz/shared";

const SORT_OPTS = [
  {v: 1, k: "Popular" as const},
  {v: 2, k: "Price ascending" as const},
  {v: 3, k: "Price descending" as const},
  {v: 4, k: "Brand A-Z" as const},
  {v: 5, k: "Brand Z-A" as const},
  {v: 6, k: "Name A-Z" as const},
  {v: 7, k: "Name Z-A" as const},
  {v: 8, k: "Newest" as const}
];

export function ProductListing({
  params,
  title,
  titleKey,
  showFilters = true,
  infiniteScroll = true,
  defaultSort = 3
}: {
  params: ProductListParams;
  title?: string;
  titleKey?: I18nKey;
  showFilters?: boolean;
  infiniteScroll?: boolean;
  defaultSort?: number;
}) {
  const {t} = useI18n();
  const [items, setItems] = useState<Product[]>([]);
  const [page, setPage] = useState(params.page || 1);
  const [sort, setSort] = useState(params.sort || defaultSort);
  const [filters, setFilters] = useState<Record<string, number[]>>(params.filters || {});
  const [pretMin, setPretMin] = useState<number | undefined>(params.pretMin);
  const [pretMax, setPretMax] = useState<number | undefined>(params.pretMax);
  const [filterGroups, setFilterGroups] = useState<CatalogFilterGroup[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const hideBrandFilter = Boolean(params.list?.startsWith("brand:"));
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const baseParams = useMemo(() => ({
    ...params,
    sort,
    filters,
    pret: [] as number[],
    pretMin,
    pretMax,
    pageSize: params.pageSize || 36
  }), [params, sort, filters, pretMin, pretMax]);
  const displayTitle = titleKey ? t(titleKey) : title;
  const showCategoryHero = Boolean(params.c);
  const heroMeta = useMemo(() => {
    if(!showCategoryHero) return null;
    return getCategoryHeroMeta(categories, {
      c: params.c,
      sub: params.sub,
      subsub: params.subsub,
      subsubsub: params.subsubsub
    }, displayTitle || "");
  }, [showCategoryHero, categories, params.c, params.sub, params.subsub, params.subsubsub, displayTitle]);
  useDocumentTitle(heroMeta?.title || displayTitle);
  const load = useCallback((p: number, append: boolean) => {
    if(append) setLoadingMore(true);
    else setLoading(true);
    const req = {...baseParams, page: p};
    return listProductsInfinite(req)
      .then((r) => {
        setItems((prev) => append ? [...prev, ...r.items] : r.items);
        setTotal(r.total);
        setPage(r.page);
        setHasMore(r.hasMore ?? r.page < r.totalPages);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, [baseParams]);
  useEffect(() => {
    setPage(1);
    load(1, false);
  }, [baseParams, load]);
  useEffect(() => {
    if(!showFilters) return;
    getCategories().then((r) => setCategories(r.items)).catch(() => {});
  }, [showFilters]);
  useEffect(() => {
    if(!infiniteScroll || !hasMore || loading || loadingMore) return;
    const el = sentinel.current;
    if(!el) return;
    const obs = new IntersectionObserver((entries) => {
      if(entries[0]?.isIntersecting && hasMore && !loadingMore) load(page + 1, true);
    }, {rootMargin: "200px"});
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadingMore, page, infiniteScroll, load]);
  function onFilterChange(next: FilterChange) {
    setFilters(next.filters);
    setPretMin(next.pretMin);
    setPretMax(next.pretMax);
    setPage(1);
  }
  const onFilterMeta = useCallback((groups: CatalogFilterGroup[]) => {
    setFilterGroups(groups);
  }, []);
  function removeFacet(fid: number, oid: number) {
    const key = String(fid);
    const cur = (filters[key] || []).filter((id) => id !== oid);
    const next = {...filters};
    if(cur.length) next[key] = cur;
    else delete next[key];
    onFilterChange({filters: next, pret: [], pretMin, pretMax});
  }
  function removePriceRange() {
    onFilterChange({filters, pret: [], pretMin: undefined, pretMax: undefined});
  }
  if(error) return <p className="round" style={{padding: 12, background: "#fee"}}>{error}</p>;
  return (
    <div className={showCategoryHero ? "cc-categoryPage" : undefined}>
      {showCategoryHero && heroMeta ? (
        <CategoryPageHero parentLabel={heroMeta.parentLabel} title={heroMeta.title} links={heroMeta.links} />
      ) : null}
      <div className="categorie cc-filtersLayout">
      {showFilters ? (
        <ProductFilters
          params={params}
          filters={filters}
          pretMin={pretMin}
          pretMax={pretMax}
          onChange={onFilterChange}
          onMeta={onFilterMeta}
          showCategories={!params.c && !params.s && !hideBrandFilter}
          hideBrandFilter={hideBrandFilter}
        />
      ) : null}
      <div id="categorieLeft" className={`cc-filtersLayout__main${showFilters ? "" : " single"}`}>
        {!showCategoryHero && displayTitle ? <h1 className="bx-h1">{displayTitle}</h1> : null}
        {showFilters && !showCategoryHero ? (
          <CategorySubnavGrid categories={categories} c={params.c} sub={params.sub} subsub={params.subsub} />
        ) : null}
        {showFilters ? (
          <ProductActiveFilters
            filters={filters}
            pretMin={pretMin}
            pretMax={pretMax}
            groups={filterGroups}
            hideBrandFilter={hideBrandFilter}
            onRemoveFacet={removeFacet}
            onRemovePriceRange={removePriceRange}
          />
        ) : null}
        <DsListingToolbar
          count={total}
          countLabel={t("products")}
          sortValue={sort}
          sortOptions={SORT_OPTS.map((o) => ({value: o.v, label: t(o.k)}))}
          onSortChange={(v) => { setSort(v); setPage(1); }}
        />
        {loading && !items.length ? <ProductGridSkeleton count={8} /> : null}
        {!loading && !items.length ? (
          <DsEmptyState title={t("No products found")} />
        ) : null}
        <div className="cc-productGridWrap">
          <div className="cc-productGrid">
            {items.map((p) => <ProductCard key={`${p.id}-${p.url}`} product={p} showWishlist />)}
          </div>
        </div>
        {items.length > 0 && hasMore ? (
          <div className="cc-loadMore">
            <p className="cc-loadMore__count"><b>{items.length}</b> / {total} {t("products")}</p>
            <button
              type="button"
              className={`cc-loadMore__btn${loadingMore ? " isLoading" : ""}`}
              disabled={loadingMore}
              onClick={() => load(page + 1, true)}
            >
              {loadingMore ? (
                <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> {t("Loading products...")}</>
              ) : (
                <><span>{t("Load more products")}</span><i className="fa fa-chevron-down" aria-hidden="true" /></>
              )}
            </button>
          </div>
        ) : null}
        {infiniteScroll && hasMore ? <div ref={sentinel} style={{height: 1}} aria-hidden="true" /> : null}
      </div>
    </div>
    </div>
  );
}
